import { useState } from "react";
import { STATUS_STYLES, JOB_TYPE_LABELS, fmtTime, fmtDate } from "@/lib/helpers";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Plane, User, Clock, Mic, Check } from "lucide-react";
import JobWeather from "@/components/JobWeather";

export default function JobCard({ job, employee, onClick, showDate, showWeather, empMap }) {
  const st = STATUS_STYLES[job.status] || STATUS_STYLES.scheduled;
  const [loaded, setLoaded] = useState(!!job.avcrm_loaded);
  const [busy, setBusy] = useState(false);

  const toggleAvcrm = async (e) => {
    e.stopPropagation();
    if (busy) return;
    const next = !loaded;
    setLoaded(next);
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}/avcrm`, { avcrm_loaded: next });
      job.avcrm_loaded = next;
      toast.success(next ? "Marked as loaded to AVCRM" : "Marked as not in AVCRM");
    } catch (err) {
      setLoaded(!next);
      toast.error("Could not update AVCRM status");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      data-testid={`job-card-${job.id}`}
      className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 transition-all hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5 cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-heading font-semibold text-slate-900 truncate">{job.title}</h4>
            {job.source === "voice" && <Mic className="h-3.5 w-3.5 text-lime-500 shrink-0" />}
          </div>
          {job.client_name && <p className="text-sm text-slate-500 truncate">{job.client_name}</p>}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${st.bg} ${st.text} ${st.border}`}>
            {st.label}
          </span>
          <button
            type="button"
            onClick={toggleAvcrm}
            disabled={busy}
            data-testid={`avcrm-toggle-${job.id}`}
            title={loaded ? "Loaded to AVCRM — click to unmark" : "Not in AVCRM — click to mark loaded"}
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border transition-all ${
              loaded ? "bg-lime-50 border-lime-300 text-lime-800" : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300"
            }`}
          >
            <span className={`h-3 w-3 rounded-[3px] flex items-center justify-center border ${loaded ? "bg-lime-500 border-lime-500" : "bg-white border-slate-300"}`}>
              {loaded && <Check className="h-2 w-2 text-white" strokeWidth={3} />}
            </span>
            In AVCRM
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          {showDate && <span>{fmtDate(job.start)} · </span>}{fmtTime(job.start)}
        </span>
        {job.location && (
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" /> <span className="truncate">{job.location}</span>
          </span>
        )}
        {job.drone_type && (
          <span className="inline-flex items-center gap-1.5">
            <Plane className="h-3.5 w-3.5 text-slate-400" /> {job.drone_type}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-start justify-between gap-2">
        <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
          {JOB_TYPE_LABELS[job.job_type] || job.job_type}
        </span>
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs text-slate-600">
          {employee && (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: employee.color }} />
              <span className="font-medium">{employee.name}</span>
              <span className="text-[9px] font-bold uppercase tracking-wide text-lime-600 bg-lime-50 border border-lime-200 rounded px-1 py-px">PIC</span>
            </span>
          )}
          {(job.assistant_ids || []).map((aid) => {
            const a = empMap?.[aid];
            if (!a) return null;
            return (
              <span key={aid} className="inline-flex items-center gap-1.5 text-slate-500">
                <span className="h-2 w-2 rounded-full" style={{ background: a.color }} />
                {a.name}
              </span>
            );
          })}
        </div>
      </div>

      {showWeather && <JobWeather location={job.location} dateISO={job.start} />}
    </div>
  );
}
